// ===================================================
// src/components/Planning/EditDayModal.js - Updated version
// ===================================================
import React, { useEffect, useState } from 'react';
import { X, Calendar, Target, Package, Factory, Truck, Edit } from 'lucide-react';
import { calculateTotals, populateFormDataFromDay, getDefaultFormData, commonModalStyles } from './utils/planningHelpers';

const EditDayModal = ({ isOpen, onClose, onSave, loading = false, dayData = null }) => {
  const [formData, setFormData] = useState(getDefaultFormData());
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');

  // Load existing data when modal opens
  useEffect(() => {
    if (isOpen && dayData) {
      console.log('Loading day data for edit:', dayData);
      const populatedData = populateFormDataFromDay(dayData);
      setFormData(populatedData);
    }
  }, [isOpen, dayData]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const processedValue = type === 'number' ? parseInt(value) || 0 : value;

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.actual_date) newErrors.actual_date = 'Date is required';
    if (formData.target_total <= 0) newErrors.target_total = 'Target must be greater than 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error updating day:', error);
    }
  };

  const { salesTotal, manufacturingTotal, stockTotal, stockTotalWithAmazon } = calculateTotals(formData);

  if (!isOpen) return null;

  const tabStyle = (tabId) => ({
    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem',
    border: 'none', borderBottom: activeTab === tabId ? '2px solid #3b82f6' : '2px solid transparent',
    background: 'none', cursor: 'pointer', fontSize: '0.875rem',
    color: activeTab === tabId ? '#3b82f6' : '#6b7280',
    fontWeight: activeTab === tabId ? '600' : '400'
  });

  return (
    <div style={commonModalStyles.modalOverlay} onClick={onClose}>
      <div style={commonModalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.5rem 1.5rem 0 1.5rem', borderBottom: '1px solid #e5e7eb',
          marginBottom: '1.5rem', paddingBottom: '1rem'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Edit Planning Day {dayData ? new Date(dayData.date).toLocaleDateString() : ''}
          </h2>
          <button onClick={onClose} disabled={loading} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0.5rem', borderRadius: '6px', color: '#6b7280'
          }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '0 1.5rem', flex: 1, overflowY: 'auto' }}>
          {/* Current Values Display */}
          <div style={{
            background: '#f8fafc', padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Current Values:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: '#6b7280' }}>Target Total:</span>
                <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>{formData.target_total}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>Sales Total:</span>
                <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>{salesTotal}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>Manufacturing Total:</span>
                <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>{manufacturingTotal}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>Total Stock:</span>
                <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>{stockTotalWithAmazon}</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
            <button type="button" onClick={() => setActiveTab('basic')} style={tabStyle('basic')}>
              <Calendar size={16} /> Basic
            </button>
            <button type="button" onClick={() => setActiveTab('sales')} style={tabStyle('sales')}>
              <Target size={16} /> Sales
            </button>
            <button type="button" onClick={() => setActiveTab('manufacturing')} style={tabStyle('manufacturing')}>
              <Factory size={16} /> Manufacturing
            </button>
            <button type="button" onClick={() => setActiveTab('stock')} style={tabStyle('stock')}>
              <Package size={16} /> Stock
            </button>
            <button type="button" onClick={() => setActiveTab('shipment')} style={tabStyle('shipment')}>
              <Truck size={16} /> Shipment
            </button>
          </div>

          {/* Tab Content */}
        {activeTab === 'basic' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              {/* Date */}
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>Date *</label>
                <input
                  type="date"
                  name="actual_date"
                  value={formData.actual_date}
                  onChange={handleChange}
                  required
                  disabled
                  style={{
                    ...commonModalStyles.inputStyle(errors.actual_date),
                    backgroundColor: '#f9fafb',
                    cursor: 'not-allowed'
                  }}
                />
                {errors.actual_date && (
                  <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{errors.actual_date}</span>
                )}
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Date cannot be changed in edit mode
                </p>
              </div>

              {/* Sales Target */}
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>Sales Target *</label>
                <input
                  type="number"
                  name="target_total"
                  value={formData.target_total}
                  onChange={handleChange}
                  min="0"
                  required
                  style={commonModalStyles.inputStyle(errors.target_total)}
                />
                {errors.target_total && (
                  <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{errors.target_total}</span>
                )}
              </div>

              {/* ✅ NEW: Production Target */}
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>Production Target</label>
                <input
                  type="number"
                  name="production_target_total"
                  value={formData.production_target_total || 0}
                  onChange={handleChange}
                  min="0"
                  style={commonModalStyles.inputStyle()}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Set total planned production units for the day
                </p>
              </div>
            </div>


              <div style={{
                background: '#f8fafc', padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Auto-calculated Values:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
                  <div>
                    <span style={{ color: '#6b7280' }}>Sales Total:</span>
                    <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>{salesTotal}</span>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>Delta:</span>
                    <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>{salesTotal - formData.target_total}</span>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>US/EU Split:</span>
                    <span style={{ fontWeight: '600', marginLeft: '0.5rem' }}>
                      {Math.round(salesTotal * 0.4)}/{salesTotal - Math.round(salesTotal * 0.4)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sales' && (
            <div>
              <div style={{
                background: '#f0fdf4', padding: '0.75rem', borderRadius: '6px',
                marginBottom: '1rem', border: '1px solid #bbf7d0'
              }}>
                <span style={{ fontWeight: '600', color: '#166534' }}>Sales Total: {salesTotal}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                {[
                  { key: 'sales_us_cityplay_m', label: 'US Cityplay M' },
                  { key: 'sales_us_cityplay_l', label: 'US Cityplay L' },
                  { key: 'sales_us_20_m', label: 'US 2.0 M' },
                  { key: 'sales_us_20_l', label: 'US 2.0 L' },
                  { key: 'sales_uk_cityplay_m', label: 'EU Cityplay M' },
                  { key: 'sales_uk_cityplay_l', label: 'EU Cityplay L' },
                  { key: 'sales_uk_20_m', label: 'EU 2.0 M' },
                  { key: 'sales_uk_20_l', label: 'EU 2.0 L' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      {field.label}
                    </label>
                    <input type="number" name={field.key} value={formData[field.key]}
                      onChange={handleChange} min="0" style={commonModalStyles.smallInputStyle} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'manufacturing' && (
            <div>
              <div style={{
                background: '#fef3c7', padding: '0.75rem', borderRadius: '6px',
                marginBottom: '1rem', border: '1px solid #fcd34d'
              }}>
                <span style={{ fontWeight: '600', color: '#92400e' }}>Manufacturing Total: {manufacturingTotal}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                {[
                  { key: 'manufacturing_us_cityplay_m', label: 'US Cityplay M' },
                  { key: 'manufacturing_us_cityplay_l', label: 'US Cityplay L' },
                  { key: 'manufacturing_us_20_m', label: 'US 2.0 M' },
                  { key: 'manufacturing_us_20_l', label: 'US 2.0 L' },
                  { key: 'manufacturing_uk_cityplay_m', label: 'EU Cityplay M' },
                  { key: 'manufacturing_uk_cityplay_l', label: 'EU Cityplay L' },
                  { key: 'manufacturing_uk_20_m', label: 'EU 2.0 M' },
                  { key: 'manufacturing_uk_20_l', label: 'EU 2.0 L' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      {field.label}
                    </label>
                    <input type="number" name={field.key} value={formData[field.key]}
                      onChange={handleChange} min="0" style={commonModalStyles.smallInputStyle} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'stock' && (
            <div>
              <div style={{
                background: '#eff6ff', padding: '0.75rem', borderRadius: '6px',
                marginBottom: '1rem', border: '1px solid #bfdbfe'
              }}>
                <span style={{ fontWeight: '600', color: '#1e40af' }}>
                  Warehouse Stock: {stockTotal} | Amazon Stock: {(formData.stock_us_amazon || 0) + (formData.stock_eu_amazon || 0)} | Total: {stockTotalWithAmazon}
                </span>
              </div>

              <h4 style={{ color: '#374151', marginBottom: '0.5rem', marginTop: '1rem' }}>Warehouse Stock</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                {[
                  { key: 'stock_us_cityplay_m', label: 'US Cityplay M' },
                  { key: 'stock_us_cityplay_l', label: 'US Cityplay L' },
                  { key: 'stock_us_20_m', label: 'US 2.0 M' },
                  { key: 'stock_us_20_l', label: 'US 2.0 L' },
                  { key: 'stock_uk_cityplay_m', label: 'EU Cityplay M' },
                  { key: 'stock_uk_cityplay_l', label: 'EU Cityplay L' },
                  { key: 'stock_uk_20_m', label: 'EU 2.0 M' },
                  { key: 'stock_uk_20_l', label: 'EU 2.0 L' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      {field.label}
                    </label>
                    <input type="number" name={field.key} value={formData[field.key]}
                      onChange={handleChange} min="0" style={commonModalStyles.smallInputStyle} />
                  </div>
                ))}
              </div>

              <h4 style={{ color: '#374151', marginBottom: '0.5rem' }}>Amazon Stock</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    🇺🇸 Stock US Amazon
                  </label>
                  <input type="number" name="stock_us_amazon" value={formData.stock_us_amazon || 0}
                    onChange={handleChange} min="0" style={commonModalStyles.inputStyle()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    🇪🇺 Stock EU Amazon
                  </label>
                  <input type="number" name="stock_eu_amazon" value={formData.stock_eu_amazon || 0}
                    onChange={handleChange} min="0" style={commonModalStyles.inputStyle()} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'shipment' && (
            <div>
              <h4 style={{ color: '#374151', marginBottom: '1rem' }}>Shipment Durations (Business Days)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                {[
                  { key: 'china_us', label: 'China → US', desc: 'Days from China to US warehouse' },
                  { key: 'china_uk', label: 'China → EU', desc: 'Days from China to EU warehouse' },
                  { key: 'us_customer', label: 'US → Customer', desc: 'Days from US warehouse to customer' },
                  { key: 'uk_customer', label: 'EU → Customer', desc: 'Days from EU warehouse to customer' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      {field.label}
                    </label>
                    <input type="number" name={field.key} value={formData[field.key] || 0}
                      onChange={handleChange} min="0" style={commonModalStyles.inputStyle()} />
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      {field.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
          padding: '1.5rem', borderTop: '1px solid #e5e7eb'
        }}>
          <button onClick={onClose} disabled={loading} style={commonModalStyles.cancelButtonStyle}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} style={commonModalStyles.saveButtonStyle(loading)}>
            {loading ? 'Updating...' : 'Update Day'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDayModal;